import { gradientsC, IsOrangeLight, themeC } from "../theme/themehelper";

export interface SurvivorThemeFlags {
    isGoGo: boolean;
    isGreen: boolean;
    isKryss: boolean;
    isLyllo: boolean;
    isDbet: boolean;
    isOrange: boolean;
    isOrangeLight: boolean;
    isWood: boolean;
    isLion: boolean;
    isHajper: boolean;
    isStugan: boolean;
    isSnabbare: boolean;
    isHard: boolean;
}

export interface SurvivorThemeColors {
    pageBg: string;
    cardBg: string;
    cardInnerBg: string;
    chartBg: string;
    textPrimary: string;
    textSecondary: string;
    textOnRibbon: string;
    accent: string;
    accentLight: string;
    signSelected: string;
    signSelectedText: string;
    signBorder: string;
    signBg: string;
    signColor: string;
    signCorrectBorder: string;
    signWrongText: string;
    liveBorder: string;
    doubleBorderColor: string;
    fixtureBorder: string;
    chartBorderGradient: string;
    chartTopBg: string;
    chartTopBorder: string;
    shareButtonBorder: string;
    shareButtonText: string;
    shareButtonIcon: string;
    countdownPillBg: string;
    tableBorderColor: string;
    tableHeaderBg: string;
    tableBodyBg: string;
    fontFamily: string;
}

export const getSurvivorThemeFlags = (theme: string): SurvivorThemeFlags => ({
    isGoGo: theme === themeC.GoGo,
    isGreen: theme === themeC.Green,
    isKryss: theme === themeC.Kryss,
    isLyllo: theme === themeC.Lyllo,
    isDbet: theme === themeC.Dbet,
    isOrange: theme === themeC.Orange,
    isOrangeLight: IsOrangeLight(theme),
    isWood: theme === themeC.Wood,
    isLion: theme === themeC.Lion,
    isHajper: theme === themeC.Hajper,
    isStugan: theme === themeC.Stugan,
    isSnabbare: theme === themeC.Snabbare,
    isHard: theme === themeC.Hard,
});

export const getSurvivorThemeColors = (flags: SurvivorThemeFlags): SurvivorThemeColors => {
    const { isGreen, isKryss, isLyllo, isDbet, isOrange, isOrangeLight, isWood, isLion, isHajper, isStugan, isSnabbare, isHard } = flags;


    return isHard ? {
        // Bethard (hard theme). Derived from src/theme/hard.css and the Tipsraketen
        // plate (bg #0E2B17, brush stroke #FFD801). Provisional; tune visually.
        pageBg: '#151515',
        cardBg: '#212121',
        cardInnerBg: '#212121',
        chartBg: '#151515',
        textPrimary: '#FDFDFD',
        textSecondary: 'rgba(253, 253, 253, 0.45)',
        textOnRibbon: '#FFFFFF',
        accent: '#FFD700',
        accentLight: '#FFD801',
        signSelected: '#FFD700',
        signSelectedText: '#151515',
        signBorder: 'none',
        signBg: '#292929',
        signColor: '#FDFDFD',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(0, 0, 0, 0.38)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#212121',
        fixtureBorder: '1px solid #292929',
        chartBorderGradient: gradientsC.Hard.Flex,
        chartTopBg: '#151515',
        chartTopBorder: '1px solid #292929',
        shareButtonBorder: '1px solid #484848',
        shareButtonText: '#FDFDFD',
        shareButtonIcon: '#FDFDFD',
        countdownPillBg: 'rgba(0, 0, 0, 0.24)',
        tableBorderColor: '#292929',
        tableHeaderBg: '#1D1D1D',
        tableBodyBg: '#212121',
        fontFamily: 'var(--font-title), sans-serif',
    } : isSnabbare ? {
        pageBg: '#FFFFFF',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F0F8FF',
        textPrimary: '#272B33',
        textSecondary: '#9EA2AB',
        textOnRibbon: '#FFFFFF',            // was #DCE6FF — image text is pure white
        accent: '#234BFF',
        accentLight: '#0081FF',
        signSelected: '#201CA4',
        signSelectedText: '#FFFFFF',
        signBorder: '1px solid #201CA4',
        signBg: '#FFFFFF',
        signColor: 'rgba(32, 28, 164, 0.38)',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(255, 255, 255, 0.5)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #EEEEEE',
        chartBorderGradient: gradientsC.Snabbare.Flex,
        chartTopBg: '#FFFFFF',
        chartTopBorder: '1px solid #EEEEEE',
        shareButtonBorder: '1px solid #EEEEEE',
        shareButtonText: '#272B33',
        shareButtonIcon: '#272B33',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#EEEEEE',
        tableHeaderBg: '#F0F8FF',
        tableBodyBg: '#FFFFFF',
        fontFamily: 'var(--font-title), sans-serif',

    } : isStugan ? {
        pageBg: '#FFFFFF',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F9F7F5',
        textPrimary: '#333333',
        textSecondary: '#969696',
        textOnRibbon: '#FFDFDA',
        accent: '#7C0E19',
        accentLight: '#B01620',
        signSelected: '#7C0E19',
        signSelectedText: '#FFFFFF',
        signBorder: '1px solid #7C0E19',
        signBg: '#FFFFFF',
        signColor: 'rgba(124, 14, 25, 0.38)',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(255, 255, 255, 0.5)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #EEEEEE',
        chartBorderGradient: gradientsC.Stugan.Flex,
        chartTopBg: '#FFFFFF',
        chartTopBorder: '1px solid #EEEEEE',
        shareButtonBorder: '1px solid #EEEEEE',
        shareButtonText: '#333333',
        shareButtonIcon: '#333333',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#EEEEEE',
        tableHeaderBg: '#F9F7F5',
        tableBodyBg: '#FFFFFF',
        fontFamily: 'var(--font-title), sans-serif',
    } : isDbet ? {
        pageBg: '#FFFFFF',
        cardBg: '#FDFDFD',
        cardInnerBg: '#FDFDFD',
        chartBg: '#FFFFFF',
        textPrimary: '#232323',
        textSecondary: '#5E5E5E',
        textOnRibbon: '#FFFFFF',
        accent: '#112F7C',
        accentLight: '#1840a7',
        signSelected: '#112F7C',
        signSelectedText: '#FFFFFF',
        signBorder: '1px solid #112F7C',
        signBg: '#FFFFFF',
        signColor: '#112F7C',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(255, 255, 255, 0.5)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #EEEEEE',
        chartBorderGradient: gradientsC.Dbet.Flex,
        chartTopBg: '#FFFFFF',
        chartTopBorder: '1px solid #EEEEEE',
        shareButtonBorder: '1px solid #EEEEEE',
        shareButtonText: '#232323',
        shareButtonIcon: '#232323',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#EEEEEE',
        tableHeaderBg: '#F5F5F5',
        tableBodyBg: '#FDFDFD',
        fontFamily: "'roboto', sans-serif",
    } : isLyllo ? {
        pageBg: '#E8EAEC',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#E8EAEC',
        textPrimary: '#282B33',
        textSecondary: '#9FA2AB',
        textOnRibbon: '#FFFFFF',
        accent: '#F02884',
        accentLight: '#EE72A6',
        signSelected: '#F02884',
        signSelectedText: '#FFFFFF',
        signBorder: '0px solid transparent',
        signBg: '#E8EAEC',
        signColor: '#282B33',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(0, 0, 0, 0.38)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #E8EAEC',
        chartBorderGradient: gradientsC.Lyllo.Flex,
        chartTopBg: '#E8EAEC',
        chartTopBorder: '1px solid #D5D8DC',
        shareButtonBorder: '1px solid #D5D8DC',
        shareButtonText: '#282B33',
        shareButtonIcon: '#282B33',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#D5D8DC',
        tableHeaderBg: '#E8EAEC',
        tableBodyBg: '#FFFFFF',
        fontFamily: "'Urbane', sans-serif",
    } : isKryss ? {
        pageBg: '#F2F5F8',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F2F5F8',
        textPrimary: '#000000',
        textSecondary: '#6A6C6D',
        textOnRibbon: '#FFFFFF',
        accent: '#1191D7',
        accentLight: '#0066a3',
        signSelected: '#1191D7',
        signSelectedText: '#FFFFFF',
        signBorder: '1px solid #E8EAEC',
        signBg: '#F2F5F8',
        signColor: '#000000',
        signCorrectBorder: '2px solid #2F9C30',
        signWrongText: 'rgba(0, 0, 0, 0.38)',
        liveBorder: '2px solid #2F9C30',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #E8EAEC',
        chartBorderGradient: gradientsC.Kryss.Flex,
        chartTopBg: '#F2F5F8',
        chartTopBorder: '1px solid #E8EAEC',
        shareButtonBorder: '1px solid #E8EAEC',
        shareButtonText: '#000000',
        shareButtonIcon: '#000000',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#E8EAEC',
        tableHeaderBg: '#F2F5F8',
        tableBodyBg: '#FFFFFF',
        fontFamily: "'Lexend', sans-serif",
    } : isGreen ? {
        pageBg: '#E8E8E8',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F5F5F5',
        textPrimary: '#111111',
        textSecondary: '#555555',
        textOnRibbon: '#FFFFFF',
        accent: '#147B45',
        accentLight: '#24995C',
        signSelected: '#147B45',
        signSelectedText: '#FFFFFF',
        signBorder: '0.5px solid #D0D0D0',
        signBg: '#FFFFFF',
        signColor: '#555555',
        signCorrectBorder: '2px solid #147B45',
        signWrongText: '#FFFFFF',
        liveBorder: '2px solid #147B45',
        doubleBorderColor: '#FFFFFF',
        fixtureBorder: '1px solid #E0E0E0',
        chartBorderGradient: 'linear-gradient(96.02deg, #197343 0%, #24995C 99.7%)',
        chartTopBg: '#F5F5F5',
        chartTopBorder: '1px solid #E0E0E0',
        shareButtonBorder: '1px solid #D0D0D0',
        shareButtonText: '#111111',
        shareButtonIcon: '#111111',
        countdownPillBg: 'rgba(255, 255, 255, 0.24)',
        tableBorderColor: '#E0E0E0',
        tableHeaderBg: '#F5F5F5',
        tableBodyBg: '#FFFFFF',
        fontFamily: "'roboto', sans-serif",
    } : isLion ? {
        pageBg: '#F6F6F6',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F6F6F6',
        textPrimary: '#1F1F1F',
        textSecondary: '#636363',
        textOnRibbon: '#FFFFFF',
        accent: '#FD5C3C',
        accentLight: '#FD6301',
        signSelected: '#31609A',
        signSelectedText: '#FFFFFF',
        signBorder: '0.5px solid #636363',
        signBg: '#F6F6F6',
        signColor: '#1F1F1F',
        signCorrectBorder: '2px solid #008844',
        signWrongText: '#A8A8A8',
        liveBorder: '2px solid #008844',
        doubleBorderColor: '#F6F6F6',
        fixtureBorder: '1px solid #DDDDDD',
        chartBorderGradient: gradientsC.Lion.Flex,
        chartTopBg: '#FFFFFF',
        chartTopBorder: '1px solid #EBEBEB',
        shareButtonBorder: '1px solid #DDDDDD',
        shareButtonText: '#1F1F1F',
        shareButtonIcon: '#1F1F1F',
        countdownPillBg: '#FFF3F1',
        tableBorderColor: '#DDDDDD',
        tableHeaderBg: '#F6F6F6',
        tableBodyBg: '#FFFFFF',
        fontFamily: "'Roboto', sans-serif",
    } : isOrangeLight ? {
        pageBg: '#F6F6F6',
        cardBg: '#FFFFFF',
        cardInnerBg: '#FFFFFF',
        chartBg: '#F6F6F6',
        textPrimary: '#1F1F1F',
        textSecondary: '#636363',
        textOnRibbon: '#1F1F1F',
        accent: '#FE8000',
        accentLight: '#FE9326',
        signSelected: '#173154',
        signSelectedText: '#FFFFFF',
        signBorder: 'none',
        signBg: '#EDF1F7',
        signColor: '#1E5CAF',
        signCorrectBorder: '2px solid #25852F',
        signWrongText: '#A8A8A8',
        liveBorder: '2px solid #25852F',
        doubleBorderColor: '#F5F7FB',
        fixtureBorder: '1px solid #DDDDDD',
        chartBorderGradient: 'none',
        chartTopBg: '#FFFFFF',
        chartTopBorder: '1px solid #EBEBEB',
        shareButtonBorder: '1px solid #DDDDDD',
        shareButtonText: '#1F1F1F',
        shareButtonIcon: '#1F1F1F',
        countdownPillBg: '#FFF0E0',
        tableBorderColor: '#DDDDDD',
        tableHeaderBg: '#F6F6F6',
        tableBodyBg: '#FFFFFF',
        fontFamily: "'Hind', sans-serif",
    } : isOrange ? {
        pageBg: '#0C0C0C',
        cardBg: '#1E1E1E',
        cardInnerBg: '#1E1E1E',
        chartBg: '#0C0C0C',
        textPrimary: '#F0F0F0',
        textSecondary: '#B3B3B3',
        textOnRibbon: '#1F1F1F',
        accent: '#FE8000',
        accentLight: '#FE9326',
        signSelected: '#1E88E5',
        signSelectedText: '#FFFFFF',
        signBorder: 'none',
        signBg: '#1B2942',
        signColor: '#5B9FE0',
        signCorrectBorder: '2px solid #25852F',
        signWrongText: '#A8A8A8',
        liveBorder: '2px solid #25852F',
        doubleBorderColor: '#1E1E1E',
        fixtureBorder: '1px solid #575757',
        chartBorderGradient: 'none',
        chartTopBg: '#0C0C0C',
        chartTopBorder: '1px solid #252525',
        shareButtonBorder: '1px solid #575757',
        shareButtonText: '#F0F0F0',
        shareButtonIcon: '#F0F0F0',
        countdownPillBg: '#FFF0E0',
        tableBorderColor: '#333',
        tableHeaderBg: '#252525',
        tableBodyBg: '#1E1E1E',
        fontFamily: "'Hind', sans-serif",
    } : isWood ? {
        pageBg: '#121212',
        cardBg: '#1E1E1E',
        cardInnerBg: '#1E1E1E',
        chartBg: '#0C0C0C',
        textPrimary: '#F0F0F0',
        textSecondary: '#B3B3B3',
        textOnRibbon: '#F0F0F0',
        accent: '#B19661',
        accentLight: '#B19661',
        signSelected: '#B19661',
        signSelectedText: '#0C0C0C',
        signBorder: 'none',
        signBg: '#333333',
        signColor: '#636363',
        signCorrectBorder: '2px solid #1DD064',
        signWrongText: 'rgba(0, 0, 0, 0.38)',
        liveBorder: '2px solid #1DD064',
        doubleBorderColor: '#1E1E1E',
        fixtureBorder: '1px solid #575757',
        chartBorderGradient: 'radial-gradient(100% 100% at 0% 0%, #B19661 72.6%, #1E1E1E 100%)',
        chartTopBg: '#0C0C0C',
        chartTopBorder: '1px solid #252525',
        shareButtonBorder: '1px solid #575757',
        shareButtonText: '#F0F0F0',
        shareButtonIcon: '#F0F0F0',
        countdownPillBg: 'rgba(0, 0, 0, 0.24)',
        tableBorderColor: '#333',
        tableHeaderBg: '#252525',
        tableBodyBg: '#1E1E1E',
        fontFamily: "'GT America', sans-serif",
    } : isHajper ? {
        pageBg: '#060C27',
        cardBg: '#0D194F',
        cardInnerBg: '#0D194F',
        chartBg: '#060C27',
        textPrimary: '#F0F0F0',
        textSecondary: '#B3B3B3',
        textOnRibbon: '#F0F0F0',
        accent: '#A9F7CA',
        accentLight: '#A9F7CA',
        signSelected: '#A9F7CA',
        signSelectedText: '#060C27',
        signBorder: '0.5px solid #2640C3',
        signBg: '#0D194F',
        signColor: 'rgba(255, 255, 255, 0.38)',
        signCorrectBorder: '2px solid #A9F7CA',
        signWrongText: 'rgba(6, 12, 39, 0.38)',
        liveBorder: '2px solid #A9F7CA',
        doubleBorderColor: '#0D194F',
        fixtureBorder: '1px solid #2640C3',
        chartBorderGradient: 'linear-gradient(to right, #2640C3, #0D194F)',
        chartTopBg: '#060C27',
        chartTopBorder: '1px solid #2640C3',
        shareButtonBorder: '1px solid #2640C3',
        shareButtonText: '#F0F0F0',
        shareButtonIcon: '#F0F0F0',
        countdownPillBg: 'rgba(0, 0, 0, 0.24)',
        tableBorderColor: '#2640C3',
        tableHeaderBg: '#091137',
        tableBodyBg: '#0D194F',
        fontFamily: "var(--font-title), sans-serif",
    } : {
        pageBg: '#121212',
        cardBg: '#1E1E1E',
        cardInnerBg: '#1E1E1E',
        chartBg: '#0C0C0C',
        textPrimary: '#F0F0F0',
        textSecondary: '#B3B3B3',
        textOnRibbon: '#F0F0F0',
        accent: '#F14AA7',
        accentLight: '#F14AA7',
        signSelected: '#F14AA7',
        signSelectedText: '#0C0C0C',
        signBorder: '0.5px solid #F0F0F0',
        signBg: '#1E1E1E',
        signColor: 'rgba(255, 255, 255, 0.38)',
        signCorrectBorder: '2px solid #00E68F',
        signWrongText: 'rgba(0, 0, 0, 0.38)',
        liveBorder: '2px solid #00E68F',
        doubleBorderColor: '#1E1E1E',
        fixtureBorder: '1px solid #575757',
        chartBorderGradient: 'linear-gradient(134.7deg, #B79734 1.69%, #DDB95F 17.48%, #FDEDAC 28.15%, #DDB95F 36.05%, #B79734 47.66%, #575757 88.98%)',
        chartTopBg: '#0C0C0C',
        chartTopBorder: '1px solid #252525',
        shareButtonBorder: '1px solid #575757',
        shareButtonText: '#F0F0F0',
        shareButtonIcon: '#F0F0F0',
        countdownPillBg: 'rgba(0, 0, 0, 0.24)',
        tableBorderColor: '#333',
        tableHeaderBg: '#252525',
        tableBodyBg: '#1E1E1E',
        fontFamily: "'Poppins', sans-serif",
    };
};
